import { isTradeInSession } from "../../../trading-sessions/sessionUtils";
import { net } from "../../lib/stats";
import type { LiteTrade } from "../../lib/types";

// Une session telle que renvoyée par getTradingSessionsByUser (erreurs incluses).
export interface RawSession {
  id: string;
  name?: string | null;
  startedAt: string | Date | null;
  endedAt: string | Date | null;
  isActive: boolean;
  actualDuration?: number | null;
  plannedDuration?: number | null;
  mood?: string | null;
  performance?: string | null;
  maxRunup?: number | null;
  maxDrawdown?: number | null;
  videoUrl?: string | null;
  isAutoGenerated?: boolean;
  errors?: { id: string; name: string }[];
}

export interface SessionRow {
  session: RawSession;
  trades: LiteTrade[];
  /** Somme nette (P&L + frais) des trades rattachés, comme l'Advanced. */
  pnl: number;
  wins: number;
  losses: number;
}

/**
 * Trades d'une session : même règle que l'Advanced (isTradeInSession, sur
 * l'heure d'ENTRÉE). Un trade ouvert pendant la session en fait partie même
 * s'il se clôture après sa fin.
 */
export function tradesOfSession(session: RawSession, trades: LiteTrade[]): LiteTrade[] {
  return trades
    .filter(tr => isTradeInSession({ date: tr.openedAt }, session))
    .sort((a, b) => a.openedAt.localeCompare(b.openedAt));
}

export function toRow(session: RawSession, trades: LiteTrade[]): SessionRow {
  const own = tradesOfSession(session, trades);
  let pnl = 0, wins = 0, losses = 0;
  for (const tr of own) {
    const n = net(tr);
    pnl += n;
    if (tr.closed && n > 0) wins++;
    else if (tr.closed && n < 0) losses++;
  }
  return { session, trades: own, pnl: Math.round(pnl * 100) / 100, wins, losses };
}

/** Sessions terminées, de la plus récente à la plus ancienne. */
export const isClosedSession = (s: RawSession) => !s.isActive && !!s.startedAt && !!s.endedAt;

export const byRecent = (a: RawSession, b: RawSession) =>
  new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime();

/** Durée en minutes : celle enregistrée, sinon l'écart début / fin. */
export function durationMinutes(s: RawSession): number | null {
  if (s.actualDuration) return s.actualDuration;
  if (s.startedAt && s.endedAt) return Math.round((new Date(s.endedAt).getTime() - new Date(s.startedAt).getTime()) / 60000);
  return null;
}

export const MOODS = ["excellent", "good", "neutral", "poor", "bad"] as const;
export const PERFORMANCES = ["excellent", "good", "average", "poor"] as const;
